import React from 'react';
import Icon from '@/components/ui/AppIcon';

interface InvestmentFeature {
  icon: string;
  title: string;
  description: string;
}

interface InvestmentModelProps {
  features: InvestmentFeature[];
}

const InvestmentModel: React.FC<InvestmentModelProps> = ({ features }) => {
  return (
    <section className="py-12 sm:py-16 lg:py-24 bg-surface">
      <div className="container mx-auto px-4 sm:px-6">
        <div className="max-w-6xl mx-auto">
          <div className="text-center mb-10 sm:mb-16">
            <h2 className="text-2xl sm:text-3xl lg:text-4xl font-bold text-text-primary mb-4 sm:mb-6">
              Investment & Engagement Model
            </h2>
            <p className="text-base sm:text-lg text-text-secondary max-w-3xl mx-auto leading-relaxed px-2">
              An institutional advisory engagement structured to deliver sustained value through systematic methodology and deep institutional partnership.
            </p>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-6 sm:gap-8 mb-8 sm:mb-12">
            {features.map((feature, index) => (
              <div
                key={index}
                className="bg-background rounded-lg institutional-border p-5 sm:p-8 hover:institutional-shadow transition-base"
              >
                <div className="flex items-start gap-4 sm:gap-6">
                  <div className="flex-shrink-0">
                    <div className="w-10 h-10 sm:w-12 sm:h-12 bg-institutional/10 rounded-md flex items-center justify-center">
                      <Icon name={feature.icon as any} size={20} className="text-institutional sm:hidden" />
                      <Icon name={feature.icon as any} size={24} className="text-institutional hidden sm:block" />
                    </div>
                  </div>
                  <div>
                    <h3 className="text-base sm:text-xl font-semibold text-text-primary mb-2 sm:mb-3">
                      {feature.title}
                    </h3>
                    <p className="text-xs sm:text-base text-text-secondary leading-relaxed">
                      {feature.description}
                    </p>
                  </div>
                </div>
              </div>
            ))}
          </div>

          <div className="bg-institutional text-institutional-foreground rounded-lg p-6 sm:p-8 lg:p-12">
            <div className="max-w-3xl mx-auto text-center">
              <h3 className="text-xl sm:text-2xl font-bold text-white mb-4 sm:mb-6">
                Begin with an Initial Conversation
              </h3>
              <p className="text-base sm:text-lg text-white/95 mb-6 sm:mb-8 leading-relaxed px-2">
                The program begins with a thorough diagnostic conversation to assess institutional readiness, strategic priorities, and alignment with the engagement model. This ensures mutual commitment to the systematic approach required for successful transformation.
              </p>
              <a
                href="/contact"
                className="inline-flex items-center gap-2 px-6 sm:px-8 py-3 sm:py-4 bg-institutional-foreground text-institutional font-semibold rounded-md hover:bg-institutional-foreground/90 transition-fast institutional-shadow text-sm sm:text-base"
              >
                Request Initial Conversation
                <Icon name="ArrowRightIcon" size={18} />
              </a>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default InvestmentModel;